import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, ActionButton } from '@/components/common'
import { useUIStore } from '@/store'
import { assetApi, QueuedAction } from '@/services/assetApi'
import { Clock, Wrench, AlertTriangle, CheckCircle, ArrowRight, RefreshCw } from 'lucide-react'
import clsx from 'clsx'

export default function ActionQueue() {
  const navigate = useNavigate()
  const { addNotification } = useUIStore()
  const [actions, setActions] = useState<QueuedAction[]>([])
  const [loading, setLoading] = useState(true)
  const [isFromAPI, setIsFromAPI] = useState(false)
  const [totalImpact, setTotalImpact] = useState('$0K')

  const priorityConfig = {
    critical: { color: 'text-status-critical', bg: 'bg-status-critical/10' },
    high: { color: 'text-status-warning', bg: 'bg-status-warning/10' },
    medium: { color: 'text-status-info', bg: 'bg-status-info/10' },
    low: { color: 'text-surface-400', bg: 'bg-surface-700' }
  }

  const statusIcons = {
    pending: Clock,
    scheduled: CheckCircle,
    in_progress: Wrench,
    completed: CheckCircle
  }

  // Fetch actions from API
  const fetchActions = async () => {
    setLoading(true)
    try {
      const result = await assetApi.getActionQueue()
      // Guard against undefined or non-array actions
      const actionsList = Array.isArray(result.data?.actions) ? result.data.actions : []
      setActions(actionsList)
      setIsFromAPI(!result.isMock)
      setTotalImpact(result.data?.summary?.estimated_total_impact || '$0K')
    } catch (error) {
      console.error('Error fetching action queue:', error)
      setActions([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchActions()
  }, [])

  const handleSchedule = async (action: QueuedAction) => {
    // Optimistic update
    setActions(prev => prev.map(a =>
      a.id === action.id ? { ...a, status: 'scheduled' as const } : a
    ))

    // Call API
    const result = await assetApi.scheduleAction(action.id)

    if (result.success) {
      addNotification({
        type: 'success',
        title: 'Maintenance Scheduled',
        message: `${action.action} for ${action.asset_id} has been scheduled`
      })
    } else {
      // Revert on failure
      setActions(prev => prev.map(a =>
        a.id === action.id ? { ...a, status: 'pending' as const } : a
      ))
      addNotification({
        type: 'error',
        title: 'Scheduling Failed',
        message: result.message
      })
    }
  }

  const handleViewDetails = (action: QueuedAction) => {
    // Navigate to asset intelligence page
    navigate(`/assets/${action.asset_id}`)
  }

  const handleViewAll = () => {
    navigate('/maintenance')
    addNotification({
      type: 'info',
      title: 'Navigating',
      message: 'Opening Maintenance Intelligence module'
    })
  }

  const handleRefresh = () => {
    fetchActions()
    addNotification({
      type: 'info',
      title: 'Refreshing',
      message: 'Fetching latest action queue...'
    })
  }

  const criticalCount = actions.filter(a => a.priority === 'critical').length
  const highCount = actions.filter(a => a.priority === 'high').length

  if (loading) {
    return (
      <Card title="Action Queue" subtitle="AI-prioritized maintenance actions">
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-12 bg-surface-700 rounded animate-pulse" />
          ))}
        </div>
      </Card>
    )
  }

  return (
    <Card
      title={`Action Queue${isFromAPI ? '' : ' (Cached)'}`}
      subtitle="AI-prioritized maintenance actions"
      actions={
        <div className="flex items-center gap-2">
          <ActionButton variant="ghost" size="sm" onClick={handleRefresh}>
            <RefreshCw size={14} />
          </ActionButton>
          <ActionButton variant="secondary" size="sm" onClick={handleViewAll}>
            View All
            <ArrowRight size={14} />
          </ActionButton>
        </div>
      }
    >
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-surface-700">
              <th className="text-left text-xs font-semibold text-surface-400 uppercase tracking-wider py-3 px-4">
                Priority
              </th>
              <th className="text-left text-xs font-semibold text-surface-400 uppercase tracking-wider py-3 px-4">
                Asset
              </th>
              <th className="text-left text-xs font-semibold text-surface-400 uppercase tracking-wider py-3 px-4">
                Action
              </th>
              <th className="text-left text-xs font-semibold text-surface-400 uppercase tracking-wider py-3 px-4">
                Category
              </th>
              <th className="text-left text-xs font-semibold text-surface-400 uppercase tracking-wider py-3 px-4">
                Est. Impact
              </th>
              <th className="text-left text-xs font-semibold text-surface-400 uppercase tracking-wider py-3 px-4">
                Due
              </th>
              <th className="text-left text-xs font-semibold text-surface-400 uppercase tracking-wider py-3 px-4">
                Status
              </th>
              <th className="text-right text-xs font-semibold text-surface-400 uppercase tracking-wider py-3 px-4">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {actions.map((action) => {
              const StatusIcon = statusIcons[action.status] || Clock
              const config = priorityConfig[action.priority]

              return (
                <tr
                  key={action.id}
                  className="border-b border-surface-800 hover:bg-surface-800/50 transition-colors cursor-pointer"
                  onClick={() => handleViewDetails(action)}
                >
                  <td className="py-3 px-4">
                    <span
                      className={clsx(
                        'inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold uppercase',
                        config.bg,
                        config.color
                      )}
                    >
                      {action.priority}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="font-mono text-sm text-primary-400">
                      {action.asset_id}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-surface-200">
                    {action.action}
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-sm text-surface-400">
                      {action.category}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-sm text-status-healthy">
                      {action.estimated_impact}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={clsx(
                      'text-sm',
                      action.due_date === 'Today' ? 'text-status-critical font-medium' :
                      action.due_date === 'Tomorrow' ? 'text-status-warning' :
                      'text-surface-400'
                    )}>
                      {action.due_date}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-1.5">
                      <StatusIcon size={14} className="text-surface-400" />
                      <span className="text-sm text-surface-400 capitalize">
                        {action.status.replace('_', ' ')}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-2">
                      <ActionButton
                        variant="primary"
                        size="sm"
                        onClick={() => handleSchedule(action)}
                        disabled={action.status === 'scheduled' || action.status === 'completed'}
                      >
                        {action.status === 'scheduled' ? 'Scheduled' : action.status === 'completed' ? 'Done' : 'Schedule'}
                      </ActionButton>
                      <ActionButton
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewDetails(action)}
                      >
                        Details
                      </ActionButton>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Summary Footer */}
      <div className="mt-4 pt-4 border-t border-surface-700 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <AlertTriangle size={16} className="text-status-critical" />
            <span className="text-sm text-surface-400">
              <span className="font-semibold text-surface-200">{criticalCount}</span> Critical
            </span>
          </div>
          <div className="flex items-center gap-2">
            <AlertTriangle size={16} className="text-status-warning" />
            <span className="text-sm text-surface-400">
              <span className="font-semibold text-surface-200">{highCount}</span> High
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Clock size={16} className="text-surface-500" />
            <span className="text-sm text-surface-400">
              <span className="font-semibold text-surface-200">{actions.length}</span> Total
            </span>
          </div>
        </div>
        <div className="text-sm text-surface-400">
          Est. Total Impact: <span className="font-semibold text-status-healthy">{totalImpact}</span>
        </div>
      </div>
    </Card>
  )
}
